import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ClipboardList,
  LogIn,
  LogOut,
  ShieldCheck,
  UserPlus,
  UserRound,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/base/dropdown-menu';

import {
  CAMINHO_DA_CONSULTA_POR_PROTOCOLO,
  CAMINHO_DO_ACOMPANHAMENTO,
  CAMINHO_DO_AUTOCADASTRO,
  CAMINHO_PARA_ENTRAR,
} from './AcompanhamentoContrato';
import { CHAVES_DO_ACOMPANHAMENTO } from './AcompanhamentoDados';
import { CHAVES_DA_INSCRICAO } from './apiDaInscricao';
import {
  CHAVE_DA_SESSAO_DO_INSCRITO,
  sairDoInscrito,
  type InscritoPublico,
} from './identidade/api';

/**
 * As duas pontas da sessão do inscrito nas telas de acompanhamento (D-11).
 *
 * Sem sessão, a tela não mostra nada da inscrição: manda entrar. A sessão é um
 * cookie que morre ao fechar o navegador — computador público —, e "Sair" a
 * encerra no servidor e limpa o que ficou em memória nesta aba.
 *
 * QUEM NÃO TEM CONTA TAMBÉM TEM SAÍDA. O cartão de entrar termina na consulta
 * anônima por protocolo e CPF ou CNPJ (J-02/S04): o critério de aceite da J-02
 * pede que o andamento resumido de um protocolo apareça sem conta, e até
 * 2026-09-30 nenhuma tela levava àquele endereço — ele só era alcançável por
 * quem já o conhecesse (decisão do dono, D4). O mesmo cartão aparece no detalhe
 * de um protocolo aberto sem sessão, onde a saída vale igual.
 */

export function CartaoParaEntrar({ sessaoTerminou = false }: { sessaoTerminou?: boolean }) {
  return (
    <section
      className="mx-auto w-full max-w-md px-4 py-10 sm:px-6"
      aria-labelledby="entrar-para-acompanhar"
    >
      <Card className="rounded-2xl">
        <CardHeader className="space-y-2">
          <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </div>
          <CardTitle id="entrar-para-acompanhar" className="text-xl">
            {sessaoTerminou ? 'Sua sessão terminou' : 'Entre para acompanhar sua inscrição'}
          </CardTitle>
          <CardDescription>
            {sessaoTerminou
              ? 'Por segurança, a sessão acaba depois de 30 minutos sem uso ou quando o navegador é fechado. Entre de novo para continuar.'
              : 'Use o CPF e a senha que você criou no autocadastro. A situação de cada veículo inscrito aparece aqui.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button asChild className="h-11 w-full sm:h-10">
            <Link to={CAMINHO_PARA_ENTRAR}>
              <LogIn className="h-4 w-4" aria-hidden />
              Entrar com CPF e senha
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-11 w-full sm:h-10">
            <Link to={CAMINHO_DO_AUTOCADASTRO}>
              <UserPlus className="h-4 w-4" aria-hidden />
              Ainda não me inscrevi
            </Link>
          </Button>
          <p className="border-t border-border pt-3 text-sm text-muted-foreground">
            Tem só o número do protocolo?{' '}
            <Link
              to={CAMINHO_DA_CONSULTA_POR_PROTOCOLO}
              className="rounded-sm text-link underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Consulte o andamento com o protocolo e o CPF ou CNPJ, sem entrar
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

/**
 * Encerra a sessão do inscrito no servidor e esquece, nesta aba, tudo o que
 * veio com ela: a própria sessão, o acompanhamento e a inscrição em curso.
 * Mesmo se a rede falhar, a tela não continua mostrando a inscrição.
 */
function useSairDoInscrito() {
  const cliente = useQueryClient();
  const navegar = useNavigate();
  const [saindo, setSaindo] = useState(false);

  const sair = useCallback(async () => {
    setSaindo(true);
    await sairDoInscrito().catch(() => undefined);
    cliente.removeQueries({ queryKey: CHAVES_DO_ACOMPANHAMENTO.todas });
    cliente.removeQueries({ queryKey: CHAVES_DA_INSCRICAO.todas });
    cliente.setQueryData(CHAVE_DA_SESSAO_DO_INSCRITO, null);
    navegar(CAMINHO_PARA_ENTRAR, { replace: true });
  }, [cliente, navegar]);

  return { sair, saindo };
}

/** "Sair": encerra a sessão no servidor e esquece a inscrição nesta aba. */
export function BotaoSair() {
  const { sair, saindo } = useSairDoInscrito();

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-9"
      onClick={() => void sair()}
      disabled={saindo}
    >
      <LogOut className="h-4 w-4" aria-hidden />
      Sair
    </Button>
  );
}

/**
 * O lugar do "Entrar" no cabeçalho do portal público quando há sessão do
 * inscrito (CPF e senha, fora do Keycloak — D-10). Quem entrou para acompanhar
 * a inscrição não é convidado a entrar de novo: vê que está conectado, chega às
 * próprias inscrições e sai dali mesmo.
 *
 * Não se confunde com a conta dos usuários internos: o "Entrar" do Keycloak
 * continua levando a `/login`, e quem tem sessão do Keycloak vê o atalho para a
 * própria área, nunca este menu.
 */
export function MenuDoInscrito({ inscrito }: { inscrito: InscritoPublico }) {
  const { sair, saindo } = useSairDoInscrito();
  const nome = inscrito.nome?.trim() || null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="secondary"
          className="gap-1.5"
          disabled={saindo}
          aria-label={`Minha inscrição, conectado como ${nome ?? `CPF ${inscrito.cpfMascarado}`}`}
        >
          <UserRound className="h-4 w-4" aria-hidden="true" />
          Minha inscrição
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="space-y-0.5">
          {nome && <p className="truncate text-sm font-semibold">{nome}</p>}
          <p className="truncate text-xs font-normal text-muted-foreground">
            CPF {inscrito.cpfMascarado}
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
          <Link to={CAMINHO_DO_ACOMPANHAMENTO}>
            <ClipboardList className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
            Minhas inscrições
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
          <Link to={CAMINHO_DO_AUTOCADASTRO}>
            <UserPlus className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
            Fazer ou continuar a inscrição
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer rounded-lg" onSelect={() => void sair()}>
          <LogOut className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
